import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import InsertChartOutlinedIcon from "@mui/icons-material/InsertChartOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import PushPinIcon from "@mui/icons-material/PushPin";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import moment from "moment";
import { toast } from "react-toastify";

import Langs, { Localize } from "../../app/lang/langs";
import CurriculumSidebar from "../../components/vlearn/CurriculumSidebar";
import LessonRenderer from "../../components/vlearn/LessonRenderer";
import {
    VLearnEmptyState, VLearnProgressRing,
    formatVLearnDuration, isVLearnOverdue, vlearnSurface, vlearnType
} from "../../components/vlearn/vlearnUi";
import {
    closeCourse, completeVLearnLesson, downloadVLearnCertificate, getVLearnCourse,
    listVLearnAnnouncements, markVLearnAnnouncementRead, openVLearnLesson,
    patchLessonProgressLocal, setCurrentLesson, vlearnHeartbeat
} from "./vlearnSlice";

/**
 * V-Learning — the player, which is where a learner actually takes a course.
 *
 * Three regions: a header that says what this course is and how far along you are, the
 * curriculum, and the lesson. The curriculum is a sibling of the lesson on a desktop and sits
 * above it on a phone, because a 320-pixel column beside a video on a 390-pixel screen leaves
 * room for neither.
 *
 * ── What "above it on a phone" costs, and what is done about it ──────────────────────
 *
 * Stacking alone was not enough. A full curriculum rendered at 100% width above the lesson
 * means that on a phone, every lesson change drops the learner at the top of a list they have
 * to scroll past again to reach the video they just chose — a twelve-lesson course is several
 * screens of list before any content. So on a phone the curriculum is CAPPED to half the
 * viewport and scrolls inside itself, it CLOSES when a lesson is picked, and the lesson pane
 * is scrolled into view. The learner then lands on the lesson, with "Show curriculum" one tap
 * away at the top of the pane. None of that applies from `md` up, where the column sits
 * beside the lesson and closing it would be a regression.
 *
 * ── Nothing here is the authority on anything ────────────────────────────────────────
 *
 * Every rule this screen appears to enforce is enforced again on the server. `locked` greys a
 * row out, but `openLesson` refuses it; the "mark as complete" button is hidden until the
 * minimum time is met, but `completeLesson` refuses it anyway. So the screen's job with a
 * refusal is not to prevent it — it is to explain it. A 403 that produces no visible change
 * reads as a broken button, which is why both refusals raise a toast that names the reason.
 */

const SIDEBAR_WIDTH = 320

const focusRing = {
    '&:focus-visible': {
        outline: '2px solid var(--orange, rgb(255,144,21))',
        outlineOffset: '2px'
    }
}

export default function CoursePlayer({
    idCourse, onBack, onOpenDashboard, onEditCourse, onOpenQuiz, learnerMode
}) {

    const dispatch = useDispatch()

    const course = useSelector((state) => state.vlearn.course)
    const sections = useSelector((state) => state.vlearn.sections)
    const managers = useSelector((state) => state.vlearn.managers)
    const access = useSelector((state) => state.vlearn.access)
    const enrollment = useSelector((state) => state.vlearn.enrollment)
    const announcements = useSelector((state) => state.vlearn.announcements)
    const currentLessonId = useSelector((state) => state.vlearn.currentLessonId)
    const lang = useSelector((state) => state.settings.lang)

    /*
     * `isMobile === 2` is the platform's phone flag (settings slice, ~165 callers), and the
     * media query is the belt to its braces: a desktop browser narrowed to a phone width gets
     * the same layout, which is also how this is checked during development.
     */
    const isMobileRedux = useSelector((state) => state.settings.isMobile)
    const theme = useTheme()
    const isNarrowQuery = useMediaQuery(theme.breakpoints.down('md'))
    const isPhone = isMobileRedux === 2 || isNarrowQuery

    // Open on a desktop, where it is a column beside the lesson. Closed on a phone, where it
    // is a wall in front of it — the learner opened the course to watch something.
    const [sidebarOpen, setSidebarOpen] = useState(() => !isPhone)

    /** The lesson pane, so a phone can be scrolled onto it after a lesson is chosen. */
    const lessonPaneRef = useRef(null)
    const [announcementsOpen, setAnnouncementsOpen] = useState(false)
    /** Announcement ids this session has already sent a read receipt for. */
    const [receipted, setReceipted] = useState({})
    /**
     * The course rollup as `completeLesson` last reported it.
     *
     * The slice has no reducer for that part of the response, deliberately — and refetching
     * the whole course to move a progress ring would rebuild the curriculum and lose the
     * reader's place. So the fresher number is held here and preferred over the enrolment row
     * that arrived with the page.
     */
    const [rollup, setRollup] = useState(null)

    // ── Loading and unloading ────────────────────────────────────────────────────────

    // Guarded with a ref: StrictMode mounts twice in development, and `getVLearnCourse` takes
    // the global spinner — firing it twice makes the second response fight the first.
    const loadedRef = useRef(null)
    useEffect(() => {
        if (!idCourse || loadedRef.current === idCourse) return
        loadedRef.current = idCourse
        setRollup(null)
        dispatch(getVLearnCourse({ id_course: idCourse }))
        dispatch(listVLearnAnnouncements({ id_course: idCourse }))
    }, [dispatch, idCourse])

    // The slice resets everything course-scoped together; leaving it behind means the next
    // course renders this one's curriculum until its own response lands.
    useEffect(() => () => { dispatch(closeCourse()) }, [dispatch])

    // ── Derived ──────────────────────────────────────────────────────────────────────

    /** Every lesson in curriculum order, which is what "next" and "previous" mean. */
    const flatLessons = useMemo(() => {
        const flat = []
        for (const section of (sections || [])) {
            for (const lesson of (section.lessons || [])) flat.push(lesson)
        }
        return flat
    }, [sections])

    const currentIndex = flatLessons.findIndex(lesson => lesson.id === currentLessonId)
    const currentLesson = currentIndex >= 0 ? flatLessons[currentIndex] : null
    const previousLesson = currentIndex > 0 ? flatLessons[currentIndex - 1] : null
    const nextLesson = currentIndex >= 0 && currentIndex < flatLessons.length - 1
        ? flatLessons[currentIndex + 1] : null

    const percent = rollup && rollup.percent != null
        ? rollup.percent
        : (enrollment ? Number(enrollment.progress_percent) || 0 : 0)
    const courseStatus = rollup && rollup.status
        ? rollup.status
        : (enrollment ? enrollment.status : 'not_started')
    const isCourseComplete = courseStatus === 'completed'

    const dueDate = enrollment && enrollment.due_date ? enrollment.due_date : (course ? course.due_date : null)
    const overdue = isVLearnOverdue(dueDate, courseStatus)

    const instructors = useMemo(() => (managers || [])
        .map(manager => [manager.prenom, manager.nom].filter(Boolean).join(' '))
        .filter(Boolean), [managers])

    const unreadCount = useMemo(() => (announcements || [])
        .filter(item => !item.is_read && !receipted[item.id]).length, [announcements, receipted])

    /** The lesson the overview's primary button should open. */
    const resumeLesson = useMemo(() => {
        const openable = flatLessons.filter(lesson => !lesson.locked)
        return openable.find(lesson => lesson.my_status !== 'completed') || openable[0] || null
    }, [flatLessons])

    // ── Actions ──────────────────────────────────────────────────────────────────────

    const selectLesson = useCallback(async (idLesson) => {
        const previousId = currentLessonId
        dispatch(setCurrentLesson(idLesson))
        /*
         * On a phone the curriculum sits ABOVE the lesson, so leaving it open after a pick
         * leaves the learner looking at the list rather than at what they picked. Closing it
         * and scrolling the pane into view is the whole of the phone navigation story.
         *
         * `requestAnimationFrame` because the pane has not been laid out at its new height
         * until after this render commits, and scrolling to a stale offset lands nowhere.
         * `prefers-reduced-motion` is honoured — a long smooth scroll is exactly the kind of
         * movement that preference is about.
         */
        if (isPhone) {
            setSidebarOpen(false)
            requestAnimationFrame(() => {
                const pane = lessonPaneRef.current
                if (!pane || !pane.scrollIntoView) return
                const reduced = typeof window !== 'undefined'
                    && window.matchMedia
                    && window.matchMedia('(prefers-reduced-motion: reduce)').matches
                pane.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' })
            })
        }
        // Which door the user came through, which the server cannot work out for itself.
        // `learnerMode` is set when the course was opened from "My learning", so its inverse
        // is an author previewing. The sequential gate exempts a previewing author and applies
        // to everyone else — including a manager taking their own course, who used to skip it
        // purely because they had edit rights.
        const response = await dispatch(openVLearnLesson({
            id_lesson: idLesson, preview: !learnerMode
        }))
        const error = response.payload && response.payload.responseError
        if (!error) return

        /*
         * A 403 is read from the server's `reason`, NOT assumed to be the sequential rule.
         *
         * `openLesson` refuses for more than one cause, and this branch used to blame
         * sequencing for all of them — so an author previewing an unpublished course was told
         * "This course is taken in order" about a course whose order toggle was off. The
         * server now names the reason; anything unrecognised gets the generic message rather
         * than a confident wrong one.
         */
        if (error.status === 403) {
            dispatch(setCurrentLesson(previousId))
            const reason = error.data && error.data.reason
            if (reason === 'sequential') {
                // The message carries the blocking lesson's title when the server sent one.
                const named = error.data && error.data.error
                toast.error(named || Localize('VLEARN_LOCKED_SEQUENTIAL', lang))
            } else if (reason === 'no_access') {
                toast.error(Localize('VLEARN_LESSON_NO_ACCESS', lang))
            } else {
                /*
                 * An UNLABELLED 403. Every refusal this endpoint can produce carries a
                 * `reason`, so reaching here means the API is not running this version of the
                 * code — most often a dev backend that was not restarted, since `node
                 * api/app.js` does not reload the way the CRA dev server does.
                 *
                 * Naming a specific cause here would repeat the mistake this whole branch
                 * exists to fix: the previous version asserted "this course is taken in
                 * order" for every 403, including ones that had nothing to do with ordering.
                 * An honest "could not be opened" is better than a confident wrong reason.
                 */
                toast.error(Localize('VLEARN_LESSON_OPEN_FAILED', lang))
            }
            return
        }
        toast.error(Localize('VLEARN_LESSON_OPEN_FAILED', lang))
    }, [dispatch, currentLessonId, lang, learnerMode, isPhone])

    /**
     * The reading/watching heartbeat.
     *
     * Fire-and-forget: the thunk has no reducer and the server's accumulated total is the
     * authority, so there is nothing here to do with the response. The server also clamps the
     * seconds it accepts against real elapsed time, which is why an interval-driven caller
     * like `YouTubeLesson` cannot inflate a completion.
     */
    const onProgress = useCallback((seconds, positionSeconds) => {
        if (!currentLessonId || !seconds) return
        // An author previewing their own course is not attendance: `heartbeat` requires
        // `canLearn` and would refuse this every few seconds, and recording it would put the
        // author's own preview time into the figures they are about to read.
        if (!access.canLearn) return
        dispatch(vlearnHeartbeat({
            id_lesson: currentLessonId,
            preview: !learnerMode,
            seconds: Math.round(seconds),
            position_seconds: positionSeconds == null ? undefined : Math.round(positionSeconds)
        }))
    }, [dispatch, currentLessonId, access.canLearn, learnerMode])

    const markComplete = useCallback(async () => {
        if (!currentLesson) return
        const idLesson = currentLesson.id
        const response = await dispatch(completeVLearnLesson({
            id_lesson: idLesson, preview: !learnerMode
        }))
        const payload = response.payload || {}
        const error = payload.responseError

        if (error) {
            const data = error.data || {}
            if (error.status === 422 && data.seconds_remaining != null) {
                toast.info(Localize('VLEARN_MIN_TIME_REMAINING', lang)
                    .replace('%time%', formatVLearnDuration(data.seconds_remaining) || ''))
            } else if (error.status === 422 && currentLesson.type === 'quiz') {
                toast.info(Localize('VLEARN_QUIZ_COMPLETE_VIA_QUIZ', lang))
            } else if (error.status === 403) {
                toast.error(Localize('VLEARN_LOCKED_SEQUENTIAL', lang))
            } else {
                toast.error(Localize('VLEARN_COMPLETE_FAILED', lang))
            }
            return
        }

        const data = payload.responseData
        if (!data || data.status !== 1) {
            toast.error(Localize('VLEARN_COMPLETE_FAILED', lang))
            return
        }

        dispatch(patchLessonProgressLocal({
            idLesson,
            changes: { my_status: 'completed', completed_at: new Date().toISOString() }
        }))

        // On a sequential course the lesson that follows has just been unlocked, and the
        // server said so by allowing this one. Patching its flag too keeps the sidebar
        // truthful without the refetch the local patch exists to avoid.
        if (course && Number(course.sequential) && nextLesson && nextLesson.locked) {
            dispatch(patchLessonProgressLocal({
                idLesson: nextLesson.id, changes: { locked: false }
            }))
        }

        if (data.course_progress) setRollup(data.course_progress)
        if (data.course_completed) toast.success(Localize('VLEARN_COURSE_COMPLETED', lang))
        else toast.success(Localize('VLEARN_LESSON_COMPLETED', lang))
    }, [dispatch, currentLesson, nextLesson, course, lang, learnerMode])

    const downloadCertificate = useCallback(() => {
        if (!course) return
        const slug = String(course.title || '').replace(/[^A-Za-z0-9_-]+/g, '_').slice(0, 60)
        // The response IS checked: this endpoint legitimately 422s when certificates are
        // disabled or the course is not complete, and 403s otherwise. Discarding the result
        // made "Download certificate" a click that flashed the spinner and did nothing.
        return dispatch(downloadVLearnCertificate({
            idCourse: course.id,
            filename: `${Localize('VLEARN_CERTIFICATE_FILE_PREFIX', lang)}_${slug || course.id}.pdf`
        })).then((action) => {
            const error = action && action.payload && action.payload.responseError
            if (!error) return
            const message = (error.data && error.data.error)
                || Localize('VLEARN_CERTIFICATE_FAILED', lang)
            toast.error(message)
        })
    }, [dispatch, course, lang])

    /**
     * Read receipts are sent for the announcements the drawer actually shows, once, when it
     * opens. The slice has no reducer for `markRead` — the response carries nothing worth
     * storing — so which ids have been receipted is tracked here, and the badge counts
     * against that rather than waiting for a refetch of the list.
     */
    useEffect(() => {
        if (!announcementsOpen) return
        /*
         * Only a LEARNER can be a read receipt.
         *
         * `markAnnouncementRead` requires `canLearn` and answers 403 otherwise, deliberately —
         * an author reading their own announcement is not one of the "11 of 14". Without this
         * guard an author or a dashboard-only viewer previewing a course fired a burst of
         * silent 403s on every drawer open, and cleared their own unread badge locally against
         * receipts that were never recorded.
         */
        if (!access.canLearn) return
        const unread = (announcements || []).filter(item => !item.is_read && !receipted[item.id])
        if (!unread.length) return
        setReceipted(previous => {
            const next = { ...previous }
            for (const item of unread) next[item.id] = true
            return next
        })
        for (const item of unread) {
            dispatch(markVLearnAnnouncementRead({ id_announcement: item.id }))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [announcementsOpen, announcements])

    // ── Render ───────────────────────────────────────────────────────────────────────

    if (!course) {
        /*
         * The reasoning for drawing nothing here was that `getVLearnCourse` takes the
         * application's full-screen loader — true while it is in flight, but not afterwards.
         * The slice's reducer bails on any non-`status:1` response (a 403, a deleted course, a
         * dropped connection), the spinner is dismissed, and `course` then stays null forever:
         * blank space, no message, no way back.
         */
        return (
            <Box sx={{ marginTop: '60px', padding: '20px' }}>
                <Box className="bodyCard" sx={{ margin: 0, padding: '20px' }}>
                    <VLearnEmptyState
                        title={<Langs str="VLEARN_COURSE_UNAVAILABLE_TITLE" />}
                        description={<Langs str="VLEARN_COURSE_UNAVAILABLE_DESC" />}
                        action={onBack ? (
                            <Button variant="outlined" size="small" onClick={onBack}
                                sx={{ textTransform: 'none' }}>
                                <Langs str="VLEARN_BACK_TO_CATALOG" />
                            </Button>
                        ) : null}
                    />
                </Box>
            </Box>
        )
    }

    return (
        /* See CourseCatalog for why the 60px top offset is desktop-only: below 768px the
           menu is a bottom-right floating opener and nothing occupies the top. */
        <Box sx={{
            marginTop: { xs: '14px', sm: '60px' },
            padding: { xs: '8px', sm: '20px' },
            minHeight: 'calc(100vh - 100px)'
        }}>
            <Box className="bodyCard" sx={{ margin: 0, padding: { xs: '14px', md: '20px 24px' } }}>

                {/* ── Header ──────────────────────────────────────────────────── */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                    <Button
                        size="small"
                        startIcon={<ArrowBackIcon sx={{ fontSize: 17 }} />}
                        onClick={() => onBack && onBack()}
                        sx={{
                            ...focusRing, textTransform: 'none', fontSize: '0.82rem',
                            color: 'var(--text-secondary, #4b5263)'
                        }}
                    >
                        <Langs str="VLEARN_BACK_TO_CATALOG" />
                    </Button>

                    <Box sx={{ flex: 1 }} />

                    {/* `Tooltip` is hover-only, so on touch it conveys nothing — the
                        `aria-label` below is what actually names this control for a screen
                        reader, and the badge names it visually. */}
                    <Tooltip title={<Langs str="VLEARN_ANNOUNCEMENTS" />} arrow>
                        <IconButton
                            size="small"
                            // A `size="small"` IconButton is 30px square. Under a fingertip
                            // that is a miss as often as a hit, and this is the only route to
                            // the announcements a learner is expected to read.
                            sx={{ ...focusRing, minWidth: 44, minHeight: 44 }}
                            onClick={() => setAnnouncementsOpen(true)}
                            aria-label={unreadCount
                                ? Localize(unreadCount === 1
                                    ? 'VLEARN_ANNOUNCEMENTS_UNREAD_ONE'
                                    : 'VLEARN_ANNOUNCEMENTS_UNREAD_MANY', lang).replace('%count%', unreadCount)
                                : Localize('VLEARN_ANNOUNCEMENTS', lang)}
                        >
                            <Badge
                                badgeContent={unreadCount}
                                sx={{ '& .MuiBadge-badge': { backgroundColor: 'var(--red, rgb(251,105,98))', color: '#fff' } }}
                            >
                                <NotificationsNoneIcon sx={{ fontSize: 20 }} />
                            </Badge>
                        </IconButton>
                    </Tooltip>

                    {/*
                      * `!learnerMode` as well as the right: opening a course from "My
                      * learning" is the learner journey, and an author who is also enrolled
                      * should not find Dashboard and Edit in front of them there. Both are a
                      * click away from "Courses I manage", which is the tab for that job.
                      */}
                    {access && access.canViewDashboard && !learnerMode ? (
                        <Button
                            size="small"
                            startIcon={<InsertChartOutlinedIcon sx={{ fontSize: 17 }} />}
                            onClick={() => onOpenDashboard && onOpenDashboard(course.id)}
                            sx={{
                                ...focusRing, textTransform: 'none', fontSize: '0.82rem',
                                color: 'var(--text-secondary, #4b5263)'
                            }}
                        >
                            <Langs str="VLEARN_OPEN_DASHBOARD" />
                        </Button>
                    ) : null}

                    {access && access.canEdit && !learnerMode ? (
                        <Button
                            size="small"
                            startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
                            onClick={() => onEditCourse && onEditCourse(course.id)}
                            sx={{
                                ...focusRing, textTransform: 'none', fontSize: '0.82rem',
                                color: 'var(--text-secondary, #4b5263)'
                            }}
                        >
                            <Langs str="VLEARN_EDIT_COURSE" />
                        </Button>
                    ) : null}
                </Box>

                <Box sx={{
                    display: 'flex', alignItems: 'flex-start', gap: { xs: 2, md: 3 }, flexWrap: 'wrap'
                }}>
                    <Box sx={{ flex: '1 1 320px', minWidth: 0 }}>
                        <Typography component="h1" sx={{ ...vlearnType.pageTitle }}>
                            {course.title}
                        </Typography>
                        {course.subtitle ? (
                            <Typography sx={{ ...vlearnType.body, mt: 0.5 }}>{course.subtitle}</Typography>
                        ) : null}

                        <Box sx={{
                            mt: 1.25, display: 'flex', alignItems: 'center',
                            gap: { xs: 1.5, md: 2.5 }, flexWrap: 'wrap'
                        }}>
                            {instructors.length ? (
                                <Typography sx={{ ...vlearnType.meta }}>
                                    <Langs str={instructors.length === 1
                                        ? 'VLEARN_INSTRUCTOR_ONE' : 'VLEARN_INSTRUCTOR_MANY'} />
                                    {` ${instructors.join(', ')}`}
                                </Typography>
                            ) : null}

                            {dueDate ? (
                                <Box sx={{
                                    display: 'flex', alignItems: 'center', gap: 0.5,
                                    color: overdue ? 'var(--red, rgb(251,105,98))' : 'var(--text-muted, #8b92a0)'
                                }}>
                                    <EventOutlinedIcon sx={{ fontSize: 15 }} aria-hidden="true" />
                                    <Typography sx={{
                                        fontSize: '0.75rem', color: 'inherit',
                                        fontWeight: overdue ? 600 : 400
                                    }}>
                                        <Langs
                                            str={overdue ? 'VLEARN_OVERDUE_SINCE' : 'VLEARN_DUE_ON'}
                                            params={{ date: moment(dueDate).format('DD/MM/YYYY') }}
                                        />
                                    </Typography>
                                </Box>
                            ) : null}

                            {course.is_mandatory ? (
                                <Chip
                                    size="small"
                                    label={<Langs str="VLEARN_MANDATORY" />}
                                    sx={{
                                        height: 22, borderRadius: '7px', fontSize: '0.7rem', fontWeight: 700,
                                        backgroundColor: 'rgba(251, 105, 98, 0.16)', color: '#B3271F'
                                    }}
                                />
                            ) : null}
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                        <Box sx={{ textAlign: 'center' }}>
                            <VLearnProgressRing percent={percent} size={56} thickness={5} status={courseStatus} />
                            <Typography sx={{ ...vlearnType.meta, mt: 0.5 }}>
                                <Langs str="VLEARN_PROGRESS" />
                            </Typography>
                        </Box>

                        {isCourseComplete && course.certificate_enabled ? (
                            <Button
                                size="small"
                                variant="contained"
                                startIcon={<WorkspacePremiumOutlinedIcon sx={{ fontSize: 18 }} />}
                                onClick={downloadCertificate}
                                sx={{
                                    ...focusRing, textTransform: 'none', fontSize: '0.82rem', boxShadow: 'none',
                                    backgroundColor: 'var(--green, rgb(58,156,96))',
                                    '&:hover': { backgroundColor: '#2f8551', boxShadow: 'none' }
                                }}
                            >
                                <Langs str="VLEARN_DOWNLOAD_CERTIFICATE" />
                            </Button>
                        ) : null}
                    </Box>
                </Box>

                <Divider sx={{ my: 2.5 }} />

                {/* ── Body ────────────────────────────────────────────────────── */}
                {!flatLessons.length ? (
                    <VLearnEmptyState
                        icon={<MenuBookOutlinedIcon />}
                        title={<Langs str="VLEARN_NO_CURRICULUM_TITLE" />}
                        description={<Langs str="VLEARN_NO_CURRICULUM_DESC" />}
                    />
                ) : (
                    <Box sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        alignItems: 'flex-start',
                        gap: { xs: 2, md: 3 }
                    }}>
                        {/* The curriculum. Stacked above the lesson below the `md` breakpoint —
                            beside it, a 320-pixel column would leave the video unwatchable. */}
                        {sidebarOpen ? (
                            <Box sx={{
                                width: { xs: '100%', md: SIDEBAR_WIDTH },
                                flexShrink: 0,
                                ...vlearnSurface.card,
                                /*
                                 * CAPPED ON A PHONE, and this is the fix for the worst thing
                                 * about the player on a small screen. `xs: 'none'` let a
                                 * twelve-lesson curriculum render at its full height above the
                                 * lesson, so opening a course put several screens of list
                                 * between the learner and the video. Half the viewport, with
                                 * the list scrolling inside itself, keeps the lesson within
                                 * one flick of the top.
                                 */
                                maxHeight: { xs: '50vh', md: 'calc(100vh - 280px)' },
                                overflowY: 'auto',
                                WebkitOverflowScrolling: 'touch'
                            }}>
                                <Box sx={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    padding: '12px 12px 12px 16px',
                                    borderBottom: '1px solid rgba(0,0,0,0.07)'
                                }}>
                                    <Typography component="h2" sx={{ ...vlearnType.sectionTitle }}>
                                        <Langs str="VLEARN_CURRICULUM" />
                                    </Typography>
                                    <IconButton
                                        size="small"
                                        onClick={() => setSidebarOpen(false)}
                                        aria-label={Localize('VLEARN_HIDE_CURRICULUM', lang)}
                                        sx={{ ...focusRing, minWidth: 44, minHeight: 44 }}
                                    >
                                        <ChevronLeftIcon sx={{ fontSize: 19 }} />
                                    </IconButton>
                                </Box>
                                <CurriculumSidebar
                                    sections={sections}
                                    currentLessonId={currentLessonId}
                                    onSelectLesson={selectLesson}
                                    // `Number(...)` not bare truthiness: a MySQL tinyint can
                                    // arrive as the string "0", and `!!"0"` is true — which
                                    // would tell the sidebar the course is ordered when the
                                    // author had switched that off. The server and
                                    // CourseDetailsForm both coerce the same way.
                                    sequential={!!Number(course.sequential)}
                                />
                            </Box>
                        ) : null}

                        {/* `scroll-margin-top` so `scrollIntoView` does not tuck the pane's
                            first line under the edge of the scroll container. */}
                        <Box
                            ref={lessonPaneRef}
                            sx={{ flex: 1, minWidth: 0, width: '100%', scrollMarginTop: '12px' }}
                        >
                            {!sidebarOpen ? (
                                <Button
                                    size="small"
                                    startIcon={<MenuBookOutlinedIcon sx={{ fontSize: 17 }} />}
                                    onClick={() => setSidebarOpen(true)}
                                    // On a phone this is the ONLY route back to the lesson
                                    // list, because the curriculum closes on every pick — so
                                    // it is full width and a proper 44px target there.
                                    fullWidth={isPhone}
                                    variant={isPhone ? 'outlined' : 'text'}
                                    sx={{
                                        ...focusRing, mb: 2, textTransform: 'none', fontSize: '0.82rem',
                                        minHeight: 44,
                                        justifyContent: isPhone ? 'flex-start' : 'center',
                                        borderColor: 'rgba(0,0,0,0.18)',
                                        color: 'var(--text-secondary, #4b5263)'
                                    }}
                                >
                                    <Langs str="VLEARN_SHOW_CURRICULUM" />
                                </Button>
                            ) : null}

                            {currentLesson ? (
                                <>
                                    <LessonRenderer
                                        lesson={currentLesson}
                                        course={course}
                                        onProgress={onProgress}
                                        onOpenQuiz={onOpenQuiz}
                                    />

                                    {/* ── Footer ──────────────────────────────── */}
                                    {/*
                                      * COLUMN ON A PHONE, ROW FROM `sm` UP.
                                      *
                                      * Wrapped in a row these three (Previous / Mark complete
                                      * / Next) fell into a ragged two-line block of 30px-high
                                      * `size="small"` buttons with the primary action landing
                                      * wherever the wrap put it. On a phone the lesson footer
                                      * is the whole of the navigation, so each action gets its
                                      * own full-width 44px row, in reading order.
                                      *
                                      * `column-reverse` is deliberately NOT used: it would put
                                      * Next first visually while leaving Previous first in the
                                      * DOM, which breaks tab order for a keyboard user.
                                      */}
                                    <Box sx={{
                                        mt: 3, pt: 2, borderTop: '1px solid rgba(0,0,0,0.07)',
                                        display: 'flex',
                                        flexDirection: { xs: 'column', sm: 'row' },
                                        alignItems: { xs: 'stretch', sm: 'center' },
                                        gap: 1.5, flexWrap: 'wrap',
                                        '& > .MuiButton-root': { minHeight: 44 }
                                    }}>
                                        {previousLesson && !previousLesson.locked ? (
                                            <Button
                                                size="small"
                                                startIcon={<ChevronLeftIcon sx={{ fontSize: 18 }} />}
                                                onClick={() => selectLesson(previousLesson.id)}
                                                sx={{
                                                    ...focusRing, textTransform: 'none', fontSize: '0.82rem',
                                                    color: 'var(--text-secondary, #4b5263)'
                                                }}
                                            >
                                                <Langs str="VLEARN_PREVIOUS_LESSON" />
                                            </Button>
                                        ) : null}

                                        {/* The spacer only means anything in a row. In a
                                            column a `flex: 1` child would eat the height. */}
                                        <Box sx={{ flex: 1, display: { xs: 'none', sm: 'block' } }} />

                                        {currentLesson.my_status === 'completed' ? (
                                            <Box sx={{
                                                display: 'flex', alignItems: 'center', gap: 0.5,
                                                justifyContent: { xs: 'center', sm: 'flex-start' },
                                                minHeight: 44,
                                                color: 'var(--green, rgb(58,156,96))'
                                            }}>
                                                <CheckIcon sx={{ fontSize: 18 }} aria-hidden="true" />
                                                <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: 'inherit' }}>
                                                    <Langs str="VLEARN_LESSON_COMPLETED_LABEL" />
                                                </Typography>
                                            </Box>
                                        ) : (currentLesson.type !== 'quiz' && access.canLearn) ? (
                                            /* `canLearn` as well as the type: an author
                                               previewing their own course cannot complete a
                                               lesson (completeLesson requires an enrolment),
                                               so offering the button would be a button that
                                               only ever produces an error. */
                                            <Button
                                                size="small"
                                                variant="contained"
                                                startIcon={<CheckIcon sx={{ fontSize: 17 }} />}
                                                onClick={markComplete}
                                                sx={{
                                                    ...focusRing, textTransform: 'none', fontSize: '0.82rem',
                                                    boxShadow: 'none',
                                                    backgroundColor: 'var(--green, rgb(58,156,96))',
                                                    '&:hover': { backgroundColor: '#2f8551', boxShadow: 'none' }
                                                }}
                                            >
                                                <Langs str="VLEARN_MARK_COMPLETE" />
                                            </Button>
                                        ) : null}

                                        {nextLesson ? (
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                disabled={!!nextLesson.locked}
                                                endIcon={<ChevronRightIcon sx={{ fontSize: 18 }} />}
                                                onClick={() => selectLesson(nextLesson.id)}
                                                sx={{
                                                    ...focusRing, textTransform: 'none', fontSize: '0.82rem',
                                                    borderColor: 'rgba(0,0,0,0.18)',
                                                    color: 'var(--text-primary, #111318)'
                                                }}
                                            >
                                                <Langs str="VLEARN_NEXT_LESSON" />
                                            </Button>
                                        ) : null}
                                    </Box>
                                </>
                            ) : (
                                <CourseOverview
                                    course={course}
                                    resumeLesson={resumeLesson}
                                    percent={percent}
                                    onSelectLesson={selectLesson}
                                />
                            )}
                        </Box>
                    </Box>
                )}
            </Box>

            {/* ── Announcements ───────────────────────────────────────────────── */}
            <Drawer
                anchor="right"
                open={announcementsOpen}
                onClose={() => setAnnouncementsOpen(false)}
                /*
                 * Full-bleed on a phone, and the bottom padding carries
                 * `env(safe-area-inset-bottom)`: a right-anchored drawer is full height, so
                 * its last announcement otherwise ends under the home indicator on a notched
                 * phone. The top inset matters for the same reason in landscape.
                 */
                PaperProps={{
                    sx: {
                        width: { xs: '100%', sm: 400 },
                        padding: '18px',
                        paddingTop: 'calc(18px + env(safe-area-inset-top, 0px))',
                        paddingBottom: 'calc(18px + env(safe-area-inset-bottom, 0px))'
                    }
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography component="h2" sx={{ ...vlearnType.sectionTitle }}>
                        <Langs str="VLEARN_ANNOUNCEMENTS" />
                    </Typography>
                    <IconButton
                        size="small"
                        // The only way out of a full-screen drawer on a phone, so it gets a
                        // real target rather than MUI's 30px small default.
                        sx={{ ...focusRing, minWidth: 44, minHeight: 44 }}
                        onClick={() => setAnnouncementsOpen(false)}
                        aria-label={Localize('VLEARN_CLOSE', lang)}
                    >
                        <CloseIcon sx={{ fontSize: 19 }} />
                    </IconButton>
                </Box>

                {!(announcements || []).length ? (
                    <VLearnEmptyState
                        icon={<NotificationsNoneIcon />}
                        title={<Langs str="VLEARN_NO_ANNOUNCEMENTS_TITLE" />}
                        description={<Langs str="VLEARN_NO_ANNOUNCEMENTS_DESC" />}
                    />
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {(announcements || []).map(item => (
                            <Box key={item.id} sx={{ ...vlearnSurface.panel, padding: '14px 16px' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                                    {item.is_pinned ? (
                                        <PushPinIcon
                                            sx={{ fontSize: 15, color: 'var(--orange, rgb(255,144,21))' }}
                                            role="img"
                                            aria-label={Localize('VLEARN_PINNED', lang)}
                                        />
                                    ) : null}
                                    <Typography component="h3" sx={{ ...vlearnType.cardTitle }}>
                                        {item.title}
                                    </Typography>
                                </Box>
                                <Typography sx={{ ...vlearnType.meta }}>
                                    {[
                                        [item.author_prenom, item.author_nom].filter(Boolean).join(' '),
                                        item.published_at ? moment(item.published_at).format('DD/MM/YYYY HH:mm') : null
                                    ].filter(Boolean).join(' · ')}
                                </Typography>
                                {/* The body is author-typed and stored verbatim. It is rendered
                                    as text with its line breaks preserved and never through
                                    `dangerouslySetInnerHTML`: an announcement goes to every
                                    learner on the course, so interpreting it as HTML would turn
                                    one pasted `<script>` into stored XSS with a captive
                                    audience. */}
                                <Typography sx={{
                                    ...vlearnType.body, mt: 1, whiteSpace: 'pre-wrap'
                                }}>
                                    {item.body}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                )}
            </Drawer>
        </Box>
    )
}

/**
 * What the pane shows before a lesson is chosen.
 *
 * The course's own description plus one button, because a learner arriving on a course they
 * have not started should not have to decide which of forty rows is the beginning.
 */
function CourseOverview({ course, resumeLesson, percent, onSelectLesson }) {

    const duration = formatVLearnDuration((Number(course.estimated_minutes) || 0) * 60)

    return (
        <Box sx={{ ...vlearnSurface.panel, padding: { xs: '20px', md: '28px 32px' } }}>
            <Typography component="h2" sx={{ ...vlearnType.sectionTitle }}>
                <Langs str="VLEARN_ABOUT_THIS_COURSE" />
            </Typography>

            {course.description ? (
                <Typography sx={{
                    ...vlearnType.body, mt: 1.25, whiteSpace: 'pre-wrap', maxWidth: '72ch'
                }}>
                    {course.description}
                </Typography>
            ) : null}

            {course.objectives ? (
                <>
                    <Typography sx={{ ...vlearnType.sectionTitle, mt: 2.5 }}>
                        <Langs str="VLEARN_OBJECTIVES" />
                    </Typography>
                    <Typography sx={{
                        ...vlearnType.body, mt: 0.75, whiteSpace: 'pre-wrap', maxWidth: '72ch'
                    }}>
                        {course.objectives}
                    </Typography>
                </>
            ) : null}

            {duration ? (
                <Typography sx={{ ...vlearnType.meta, mt: 2 }}>
                    <Langs str="VLEARN_ESTIMATED_TIME" params={{ duration }} />
                </Typography>
            ) : null}

            {resumeLesson ? (
                <Button
                    variant="contained"
                    size="small"
                    onClick={() => onSelectLesson(resumeLesson.id)}
                    sx={{
                        ...focusRing, mt: 2.5, textTransform: 'none', fontSize: '0.85rem', boxShadow: 'none',
                        backgroundColor: 'var(--orange, rgb(255,144,21))',
                        '&:hover': { backgroundColor: '#e07f0d', boxShadow: 'none' }
                    }}
                >
                    <Langs str={percent > 0 ? 'VLEARN_RESUME' : 'VLEARN_START'} />
                </Button>
            ) : (
                <Typography sx={{ ...vlearnType.body, mt: 2 }}>
                    <Langs str="VLEARN_SELECT_LESSON_HINT" />
                </Typography>
            )}
        </Box>
    )
}
